import React, { useEffect, useMemo, useState } from 'react';
import { useGetList } from 'react-admin';
import { Box, TextField, Button, Autocomplete } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { MEMBER_SUMMARY_STRINGS } from '../constants';
import { CrosswalkFiltersProps } from '../utils/DataTypes';

const CrosswalkFilters = ({
  selectedClient,
  onClientChange,
  onApply,
  onClear,
  setSelectedClient,
  reset,
}: CrosswalkFiltersProps) => {
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (reset) {
      setInputValue('');
      setSelectedClient(null);
    }
  }, [reset, setSelectedClient]);

  const { data: rawClientChoices = [], isLoading } = useGetList('clients', {
    filter: { report_type: '' },
    pagination: { page: 1, perPage: 5000 },
    sort: { field: 'client_name', order: 'ASC' },
  });

  const clientChoices = rawClientChoices.map((client: any) => ({
    ...client,
    billing_client_name: client.client_name,
    billing_client_number: client.client_number,
    billing_client_key_id: client.client_key_id || client.id,
  }));

  const uniqueClientChoices = Array.from(
    new Map(clientChoices.map((item) => [item.billing_client_number, item])).values()
  );

  const filteredOptions = useMemo(
    () =>
      uniqueClientChoices.filter(
        (option) =>
          (option.billing_client_name && option.billing_client_name.toLowerCase().includes(inputValue.toLowerCase())) ||
          (option.billing_client_number &&
            option.billing_client_number.toLowerCase().includes(inputValue.toLowerCase()))
      ),
    [uniqueClientChoices, inputValue]
  );

  useEffect(() => {
    if (selectedClient) {
      setInputValue(`${selectedClient.billing_client_name} (${selectedClient.billing_client_number})`);
    } else {
      setInputValue('');
    }
  }, [selectedClient]);

  return (
    <Box display="flex" alignItems="flex-end" gap={2} p={2}>
      <Autocomplete
        options={filteredOptions}
        value={selectedClient}
        onChange={(event, newValue) => onClientChange(newValue)}
        getOptionLabel={(option) =>
          `${option.billing_client_name}${
            option.billing_client_number
              ? ` (${option.billing_client_number})`
              : ` ${MEMBER_SUMMARY_STRINGS.CLIENT_ID_MISSING_LABEL}`
          }`
        }
        isOptionEqualToValue={(option, value) => option.billing_client_number === value.billing_client_number}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
        renderOption={(props, option) => (
          <li {...props} key={option.billing_client_number}>
            {`${option.billing_client_name} (${option.billing_client_number})`}
          </li>
        )}
        renderInput={(params) => <TextField {...params} label="Client Name" size="small" variant="standard" />}
        sx={{ width: 350 }}
        loading={isLoading}
        filterOptions={(x) => x}
      />
    </Box>
  );
};

export default CrosswalkFilters;
